import { TonemappingOperator } from '@babylonjs/core';

//------------------------------------------
// AntiAliasing
//------------------------------------------
export type AntialiasingOptions = {
  msaaEnabled: boolean;
  fxaaEnabled: boolean;
};
export const defaultAntialiasingOptions: AntialiasingOptions = {
  msaaEnabled: true,
  fxaaEnabled: true,
};

//------------------------------------------
// ImageProcessing
//------------------------------------------
export type ImageProcessingOptions = {
  enabled: boolean;
  contrast: number;
  exposure: number;
};
export const defaultImageProcessingOptions: ImageProcessingOptions = {
  enabled: true,
  contrast: 1,
  exposure: 1,
};

//------------------------------------------
// ImageProcessing - ColorCurve
//------------------------------------------
export type ImageProcessingColorCurveOptions = {
  enabled: boolean;
  globalDensity: number;
  globalExposure: number;
  globalHue: number;
  globalSaturation: number;
  highlightsDensity: number;
  highlightsExposure: number;
  highlightsHue: number;
  highlightsSaturation: number;
  midtonesDensity: number;
  midtonesExposure: number;
  midtonesHue: number;
  midtonesSaturation: number;
  shadowsDensity: number;
  shadowsExposure: number;
  shadowsHue: number;
  shadowsSaturation: number;
};
export const defaultImageProcessingColorCurveOptions: ImageProcessingColorCurveOptions = {
  enabled: false,
  globalDensity: 0,
  globalExposure: 0,
  globalHue: 30,
  globalSaturation: 0,
  highlightsDensity: 0,
  highlightsExposure: 0,
  highlightsHue: 30,
  highlightsSaturation: 0,
  midtonesDensity: 0,
  midtonesExposure: 0,
  midtonesHue: 30,
  midtonesSaturation: 0,
  shadowsDensity: 0,
  shadowsExposure: 0,
  shadowsHue: 30,
  shadowsSaturation: 0,
};

//------------------------------------------
// ImageProcessing - ToneMapping
//------------------------------------------
export type ToneMappingOptions = {
  enabled: boolean;
  type: TonemappingOperator;
};
export const defaultToneMappingOptions: ToneMappingOptions = {
  enabled: true,
  type: TonemappingOperator.Hable,
};

//------------------------------------------
// Bloom
//------------------------------------------
export type BloomOptions = {
  enabled: boolean;
  kernel: number;
  scale: number;
  threshold: number;
  weight: number;
};
export const defaultBloomOptions: BloomOptions = {
  enabled: false,
  kernel: 64, // 64 by default
  scale: 0.5, // 0.5 by default
  threshold: 0.8, // 0.9 by default
  weight: 0.3, // 0.15 by default
};

//------------------------------------------
// Vignette
//------------------------------------------
export type VignetteOptions = {
  enabled: boolean;
  vignetteWeight: number;
  vignetteCameraFov: number;
};
export const defaultVignetteOptions: VignetteOptions = {
  enabled: false,
  vignetteWeight: 0,
  vignetteCameraFov: 0,
};

//------------------------------------------
// SSAO
//------------------------------------------
export type SSAOOptions = {
  enabled: boolean;

  ssaoRatio: number;
  blurRatio: number;

  // SSAO
  samples: number;
  epsilon: number;

  totalStrength: number;
  base: number;
  radius: number;

  minZAspect: number;
  maxZ: number;

  // Blur
  bypassBlur: boolean;
  expensiveBlur: boolean;
  bilateralSamples: number;
  bilateralSoften: number;
  bilateralTolerance: number;
};
export const defaultSSAOOptions: SSAOOptions = {
  enabled: false,

  ssaoRatio: 1,
  blurRatio: 0.5,

  // SSAO
  samples: 16,
  epsilon: 0.02,
  totalStrength: 1.5,
  base: 0,
  radius: 2,
  minZAspect: 0.2,
  maxZ: 500,

  // Blur
  bypassBlur: false,
  expensiveBlur: true,
  bilateralSamples: 16,
  bilateralSoften: 1,
  bilateralTolerance: 1,
};

//------------------------------------------
// ALL
//------------------------------------------
type PartialOptions<T> = Partial<T>;

export type PostprocessOptions = {
  enabled: boolean;
  antialiasing: AntialiasingOptions;
  imageProcessing: ImageProcessingOptions;
  colorCurve: ImageProcessingColorCurveOptions;
  toneMapping: ToneMappingOptions;
  bloom: BloomOptions;
  vignette: VignetteOptions;
  ssao: SSAOOptions;
};
export type PostprocessParameters = {
  [K in keyof PostprocessOptions]?: PartialOptions<PostprocessOptions[K]>;
};
export const defaultPostprocessOptions = {
  enabled: true,
  antialiasing: defaultAntialiasingOptions,
  imageProcessing: defaultImageProcessingOptions,
  colorCurve: defaultImageProcessingColorCurveOptions,
  toneMapping: defaultToneMappingOptions,
  bloom: defaultBloomOptions,
  vignette: defaultVignetteOptions,
  ssao: defaultSSAOOptions,
};
